import axios from "axios";
import { getApiBaseUrl } from "./apiClient";

export type ProviderConfig = {
  provider: 'ollama' | 'openrouter';
  model: string;
  fallbackEnabled: boolean;
  fallbackModel: string;
};

export type ModelKey =
  | 'scraperAgent'
  | 'scraperNormalization'
  | 'scraperExtraction'
  | 'scraperEnrichment'
  | 'scraperCategoryInference'
  | 'fbReviewClassification'
  | 'inquiryReply'
  | 'chatAgent'
  | 'discoveryClassifier'
  | 'discoveryExtractor';

export type AppSettings = {
  auth: {
    phoneEnabled: boolean;
    claimOtpEnabled?: boolean;
  };
  fbReviewAgent?: {
    enabled: boolean;
    unmatchedStoreId?: string;
    groupPostsProvider?: 'apify' | 'scrapecreators';
    postClassificationProvider?: 'jev' | 'llm';
    vision?: { enabled?: boolean; model?: string };
  };
  scrapeCreators?: {
    apiKey?: string;
    creditCostUsd?: number;
  };
  facebookGraph?: {
    enabled?: boolean;
    accessToken?: string;
  };
  /** Comment-to-DM private replies (quick-261001-ikp). Read by the chat-agent dyno. */
  commentToDm?: {
    instagram?: boolean;
    facebook?: boolean;
    publicReplyText?: string;
    dmInvitationText?: string;
    dailyRunCap?: number;
  };
  whatsappSurvey?: {
    templateName: string;
    flowId: string;
    language: string;
  };
  categoryInference?: {
    provider?: 'jev' | 'llm';
  };
  /** Master switch for the public /blacklist and /fake-reviews routes. Off means both 404. */
  publicBlacklist?: {
    enabled?: boolean;
  };
  modelKeys: Record<ModelKey, ProviderConfig>;
  apifyKeys?: Array<{
    id: string;
    name: string;
    apiKey: string;
    budget: number;
    status: 'active' | 'expired';
    lastUsageUsd?: number;
    usageCycleFrom?: string;
    lastCheckedAt?: string;
  }>;
  apifyActors?: {
    ecommerceFacebookPage?: { apiKey?: string; apiKeyId?: string };
    ecommerceInstagramProfile?: { apiKey?: string; apiKeyId?: string };
    ecommerceTiktokProfile?: { apiKey?: string; apiKeyId?: string };
    fbGroupPosts?: { apiKey?: string; apiKeyId?: string };
    facebookReviews?: { apiKey?: string; apiKeyId?: string };
    googleMapsReviews?: { apiKey?: string; apiKeyId?: string };
  };
  chatEngine?: {
    enabledByChannel?: Partial<
      Record<"messenger" | "instagram" | "whatsapp" | "web", "v1" | "v2">
    >;
    shadow?: { enabled?: boolean; dailyCapUsd?: number; timeoutMs?: number };
    escalation?: { model?: string; onRepairExhausted?: boolean };
    // Phase 36 D-13/D-07/D-06/D-03: the deep-tool group. Surfaced in AdminAppSettings so the kill
    // switch is a UI toggle, never a curl. The controller merges leaf by leaf, so sending only
    // `deepTools` never clobbers `enabledByChannel`/`shadow`/`escalation`.
    deepTools?: {
      enabled?: boolean;
      mongoEnabled?: boolean;
      meiliRawEnabled?: boolean;
      webSearchEnabled?: boolean;
      dailyCapUsd?: number;
      maxCallsPerRun?: number;
      minReviewFloor?: number;
      maxModelTurns?: number;
    };
    // Phase 38 (declared by plan 38-02, surfaced by 38-04): the three web-transport groups. They are
    // edited from the "Web Chat Transport" card in AdminAppSettings so the abuse bounds of a public
    // anonymous surface are a UI control and never a curl. As with `deepTools` above, the controller
    // merges leaf by leaf, so sending only `webLimits` never clobbers `enabledByChannel`/`shadow`/
    // `escalation`/`deepTools`/`webSession`/`webStream`.
    //
    // Every leaf is optional because a PARTIAL send is the normal case — the card sends only the
    // groups the operator actually touched, and an untouched group is simply absent.
    //
    // HARD INPUT CONSTRAINT, and the one place these nine differ from every other leaf in the same
    // handler: `validateWebGroups` in backend/src/controllers/settingsController.ts REJECTS a
    // non-positive or non-numeric value with a 400 instead of ignoring it, because a silently
    // dropped `anonPerHour: 0` leaves an operator believing they closed a tap they did not. The 400
    // fails the WHOLE settings save, including the unrelated cards on the same page, so a cleared
    // number input must never reach here as `0`, `''` or `NaN`.
    //
    // Keep every group FLAT (scalars only): the backend's `mergeSectionLeaves` merges exactly two
    // levels, and a nested leaf would be stored as one opaque value.
    webLimits?: {
      anonPerHour?: number;
      anonPerDay?: number;
      userPerHour?: number;
      userPerDay?: number;
      perIpPerHour?: number;
    };
    webSession?: { ttlDays?: number };
    webStream?: {
      heartbeatMs?: number;
      maxBufferedChars?: number;
      maxConcurrentConnections?: number;
    };
  };
  // Quick task 260824-08c: OpenRouter low-credit alert knobs, edited from /admin/usage.
  // `lastAlertedAt` is read-only here — the backend writes it and ignores it on PUT.
  serviceAlerts?: {
    enabled: boolean;
    openRouterMinCredits: number;
    lastAlertedAt?: string;
  };
};

export type ApifyUsageSummary = {
  keyId: string;
  keyName: string;
  tokenPreview: string;
  ok: boolean;
  totalUsageUsd?: number;
  usageCycleStartAt?: string;
  usageCycleEndAt?: string;
  budget: number;
  status: 'active' | 'expired';
  error?: string;
};

export const DEFAULT_MODEL_KEYS: Record<ModelKey, ProviderConfig> = {
  scraperAgent:             { provider: 'openrouter', model: 'anthropic/claude-sonnet-4-5', fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  scraperNormalization:     { provider: 'openrouter', model: 'openai/gpt-4o-mini',           fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  scraperExtraction:        { provider: 'openrouter', model: 'openai/gpt-4o-mini',           fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  scraperEnrichment:        { provider: 'openrouter', model: 'openai/gpt-4o-mini',           fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  scraperCategoryInference: { provider: 'openrouter', model: 'openai/gpt-4o-mini',           fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  fbReviewClassification:   { provider: 'openrouter', model: 'openai/gpt-4o-mini',           fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  inquiryReply:             { provider: 'openrouter', model: 'google/gemini-3.8-flash',      fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  chatAgent:                { provider: 'openrouter', model: 'meta-llama/llama-3.3-70b-instruct', fallbackEnabled: false, fallbackModel: 'openai/gpt-4o-mini' },
  discoveryClassifier:      { provider: 'ollama',     model: 'llama3.2',                     fallbackEnabled: true,  fallbackModel: 'openai/gpt-4o-mini' },
  discoveryExtractor:       { provider: 'ollama',     model: 'llama3.2',                     fallbackEnabled: true,  fallbackModel: 'openai/gpt-4o-mini' },
};

// Mirrors `DEFAULT_CHAT_ENGINE.webLimits` / `.webSession` / `.webStream` in
// `backend/src/services/llmTypes.ts` (whose read-side twins are `DEFAULT_WEB_LIMITS_VIEW` /
// `DEFAULT_WEB_SESSION_VIEW` / `DEFAULT_WEB_STREAM_VIEW` in `chat-agent/src/services/settingsService.ts`).
// Same role as `DEEP_TOOLS_DEFAULTS` in `AdminAppSettings.tsx`: the DISPLAY fallback for a stored
// settings document that predates these groups. They live here rather than in the component because
// `DEFAULT_MODEL_KEYS` above set that precedent — a defaults literal that mirrors a backend default
// belongs beside the type that mirrors the backend shape.
//
// The backend remains the schema owner: these values are a third copy of a cross-dyno contract with
// no runtime check behind it. If a backend default moves, move it here too. Saving these exact
// numbers is a no-op write, because the controller merges leaf by leaf.
export const WEB_LIMITS_DEFAULTS = {
  anonPerHour: 10,
  anonPerDay: 40,
  userPerHour: 20,
  userPerDay: 100,
  perIpPerHour: 60,
};

export const WEB_SESSION_DEFAULTS = {
  ttlDays: 60,
};

export const WEB_STREAM_DEFAULTS = {
  heartbeatMs: 15000,
  maxBufferedChars: 8000,
  maxConcurrentConnections: 50,
};

export const getAppSettings = async (): Promise<AppSettings> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/settings`);
    return response.data.data;
  } catch (error) {
    return {
      auth: { phoneEnabled: false },
      modelKeys: { ...DEFAULT_MODEL_KEYS },
    };
  }
};

export const getAdminAppSettings = async (): Promise<AppSettings> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.get(`${getApiBaseUrl()}/admin/settings`, {
      headers: getHeaders(),
    });
    return response.data.data;
  } catch (error) {
    return getAppSettings();
  }
};

export const updateAppSettings = async (
  // This Pick is a WHITELIST — a key missing here cannot be saved from the admin UI at all.
  updates: Partial<Pick<AppSettings, 'auth' | 'fbReviewAgent' | 'whatsappSurvey' | 'categoryInference' | 'publicBlacklist' | 'modelKeys' | 'apifyActors' | 'apifyKeys' | 'chatEngine' | 'serviceAlerts' | 'scrapeCreators' | 'facebookGraph' | 'commentToDm'>>
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.put(`${getApiBaseUrl()}/admin/settings`, updates, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: 'Failed to update settings' };
  }
};

export type ApifyUsageSummaryResponse = {
  results: ApifyUsageSummary[];
  fetchedAt: number;
};

export type OpenRouterCreditsResult = {
  totalCredits: number;
  totalUsage: number;
  remaining: number;
  fetchedAt: number;
};

export const getOpenRouterCredits = async (forceRefresh = false): Promise<OpenRouterCreditsResult | null> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.get(`${getApiBaseUrl()}/admin/settings/openrouter-credits`, {
      headers: getHeaders(),
      params: forceRefresh ? { forceRefresh: 'true' } : undefined,
    });
    return response.data?.data ?? null;
  } catch {
    return null;
  }
};

export type ScrapeCreatorsCreditsResult = {
  creditCount: number;
  estimatedUsd: number;
  fetchedAt: number;
};

export const getScrapeCreatorsCredits = async (forceRefresh = false): Promise<ScrapeCreatorsCreditsResult | null> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.get(`${getApiBaseUrl()}/admin/settings/scrapecreators-credits`, {
      headers: getHeaders(),
      params: forceRefresh ? { forceRefresh: 'true' } : undefined,
    });
    return response.data?.data ?? null;
  } catch {
    return null;
  }
};

export type ImageKitUsageResult = {
  storageBytes: number;
  bandwidthBytes: number;
  fetchedAt: number;
};

export const getImageKitUsage = async (forceRefresh = false): Promise<ImageKitUsageResult | null> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.get(`${getApiBaseUrl()}/admin/settings/imagekit-usage`, {
      headers: getHeaders(),
      params: forceRefresh ? { forceRefresh: 'true' } : undefined,
    });
    return response.data?.data ?? null;
  } catch {
    return null;
  }
};

export const getApifyUsageSummary = async (forceRefresh = false): Promise<ApifyUsageSummaryResponse> => {
  try {
    const { getHeaders } = await import('./apiClient');
    const response = await axios.get(`${getApiBaseUrl()}/admin/settings/apify-usage`, {
      headers: getHeaders(),
      params: forceRefresh ? { forceRefresh: 'true' } : undefined,
    });
    const data = response.data?.data;
    // Handle both old array shape and new { results, fetchedAt } shape
    if (Array.isArray(data)) return { results: data, fetchedAt: Date.now() };
    return data ?? { results: [], fetchedAt: Date.now() };
  } catch {
    return { results: [], fetchedAt: Date.now() };
  }
};
